"use client";

import { useTranslations } from "next-intl";

import { championLabel } from "@/entities/replay-metrics";
import { ChampionIcon } from "@/shared/ui/data-display/champion-icon";
import { cn } from "@/shared/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/modals/dialog";

/**
 * KAN-900: разбор одной команды — чипы стиля, метрики и связки чемпионов.
 *
 * Раньше жил внизу витрины «Статистика команд», под таблицей сравнения. Таблицу
 * заказчик убрал, разбор переехал сюда — открывается кликом по команде в ленте
 * составов.
 *
 * ЧИПЫ И МЕТРИКИ ПРИХОДЯТ ГОТОВЫМИ, И ЭТО НЕ ЛЕНЬ. Ярлык стиля вычисляется
 * сравнением команды с МЕДИАНОЙ по всем сопоставимым командам турнира. Посчитай мы
 * его здесь, внутри модалки одной команды, сравнивать было бы не с кем — команда
 * сравнилась бы сама с собой, и чипы исчезли бы у всех разом, без ошибки и без
 * объяснения. Поэтому считает их лента, где видны все команды.
 */

export interface TeamBreakdownMetric {
  /** Ключ оси — для подписи из словаря. */
  key: string;
  /** Готовая строка значения: формат у метрик разный. */
  value: string;
}

export interface TeamBreakdownPair {
  championA: string;
  championB: string;
  /** Тип связки из справочника; null — просто часто играли вместе. */
  synergyKind: string | null;
  wins: number;
  games: number;
}

export interface TeamBreakdown {
  teamName: string;
  /** Ключи ярлыков стиля, посчитанные на уровне ленты. */
  tags: string[];
  metrics: TeamBreakdownMetric[];
  pairs: TeamBreakdownPair[];
}

/** Чипы ярлыков стиля. Общие для модалки команды и разбора матча (KAN-770). */
export function TeamStyleTagList({ tags }: { tags: string[] }) {
  const t = useTranslations("TournamentStats.teamStats");
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {tags.map((tag) => (
        <span
          key={tag}
          className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
          title={t(`styleTag.${tag}.hint`)}
        >
          {t(`styleTag.${tag}.name`)}
        </span>
      ))}
    </div>
  );
}

/** Сколько связок показываем: дальше список перестаёт читаться. */
const MAX_PAIRS = 3;

export function TeamBreakdownDialog({
  breakdown,
  open,
  onOpenChange,
}: {
  breakdown: TeamBreakdown | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const t = useTranslations("TournamentStats.teamStats");
  const axis = useTranslations("Profile.behaviorMetrics.axis");

  if (!breakdown) return null;

  const known = breakdown.pairs.filter((p) => p.synergyKind !== null);
  const pairs = (known.length > 0 ? known : breakdown.pairs).slice(0, MAX_PAIRS);
  const empty =
    breakdown.tags.length === 0 && breakdown.metrics.length === 0 && pairs.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[540px]">
        <DialogHeader>
          <DialogTitle>{breakdown.teamName}</DialogTitle>
          {/* Постоянный текст, а не подсказка: это условие чтения ВСЕХ чисел блока.
              Без него команда с шестью играми выглядит сильнее команды с двумя
              просто потому, что играла дольше. Раньше эта оговорка стояла над
              таблицей сравнения — вместе с таблицей она бы и пропала. */}
          <DialogDescription>{t("subtitle")}</DialogDescription>
        </DialogHeader>

        {empty ? (
          <p className="text-[13px] text-muted-foreground">{t("noBreakdown")}</p>
        ) : (
          <div className="flex flex-col gap-4">
            {breakdown.tags.length > 0 ? (
              <TeamStyleTagList tags={breakdown.tags} />
            ) : null}

            {breakdown.metrics.length > 0 ? (
              // Ярлык говорит «что», числа показывают «насколько».
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
                {breakdown.metrics.map((m) => (
                  <span key={m.key} className="whitespace-nowrap">
                    {axis(m.key)}{" "}
                    <span className="font-medium tabular-nums text-foreground">
                      {m.value}
                    </span>
                  </span>
                ))}
              </div>
            ) : null}

            {pairs.length > 0 ? (
              <div className="flex flex-col gap-1.5">
                <div className="text-xs font-medium text-muted-foreground">
                  {t("synergies")}
                </div>
                <div className="flex flex-wrap gap-2">
                  {pairs.map((pair) => (
                    <span
                      key={`${pair.championA}-${pair.championB}`}
                      className={cn(
                        "flex items-center gap-2 rounded-lg border px-2 py-1 text-xs",
                        pair.synergyKind
                          ? "border-primary/40 bg-primary/5"
                          : "border-border",
                      )}
                    >
                      <span className="flex shrink-0 items-center gap-0.5">
                        {[pair.championA, pair.championB].map((champ) => (
                          <ChampionIcon
                            name={champ}
                    label=""
                            size={20}
                            className="h-5 w-5 rounded object-cover"
                          />
                        ))}
                      </span>
                      <span className="flex flex-col leading-tight">
                        <span className="font-medium text-foreground">
                          {championLabel(pair.championA)} + {championLabel(pair.championB)}
                        </span>
                        {/* Два разных основания попасть в витрину, и подписывать их
                            одинаково нельзя: связка из справочника — «взяли ради
                            взаимодействия», повторяющаяся пара — «часто вместе». */}
                        <span className="text-muted-foreground">
                          <span className={pair.synergyKind ? "text-primary" : undefined}>
                            {pair.synergyKind
                              ? t(`synergyKind.${pair.synergyKind}`)
                              : t("playedTogether")}
                          </span>
                          {" · "}
                          {t("pairRecord", { wins: pair.wins, games: pair.games })}
                        </span>
                      </span>
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
